import { RBush3D } from 'rbush-3d';
import * as satellite from 'satellite.js';

const PAD = 5; // 5 km buffer
const active3DTree = new RBush3D();
const satelliteMap = new Map(); // Map of satelliteId -> RTreeBox
const satrecCache = new Map();  // Map of satelliteId -> parsed satrec (lazy)

/**
 * Normalize and convert satellite data to a 3D R-tree box.
 * Adds padding for spatial indexing and copies useful metadata.
 * TLE lines (tle_line1/tle_line2) are now embedded directly in the
 * satellite export, so they're just read straight off the incoming object —
 * no separate TLE file or merge step needed.
 * @param {Object} sat Satellite raw data object
 * @returns {Object|null} Normalized satellite box for R-tree or null if invalid
 */
function to3DBox(sat) {
  if (
    typeof sat.x_km !== 'number' ||
    typeof sat.y_km !== 'number' ||
    typeof sat.z_km !== 'number' ||
    typeof sat.altitude_km !== 'number'
  ) {
    console.warn('[R-Tree] Skipping invalid satellite data:', sat);
    return null;
  }

  const id = sat.satellite_id || sat.satelliteId || sat.id;
  if (!id) return null;

  return {
    satelliteId: id,
    name: sat.name || '',
    country: sat.country || 'unknown',
    category: sat.category || 'unknown',
    orbit_type: sat.orbit_type || 'unknown',
    object_type: sat.object_type || 'payload',
    launch: sat.launch || null,
    altitude: sat.altitude_km,
    x: sat.x_km,
    y: sat.y_km,
    z: sat.z_km,
    minX: sat.x_km - PAD,
    minY: sat.y_km - PAD,
    minZ: sat.z_km - PAD,
    maxX: sat.x_km + PAD,
    maxY: sat.y_km + PAD,
    maxZ: sat.z_km + PAD,
    lon: sat.maxLon,
    lat: sat.maxLat,
    speed: sat.velocity_km_h,
    // TLE lines embedded directly in the export — carried through as plain
    // strings so they stay cheap to diff/stringify. undefined for any
    // satellite the export didn't attach TLE data to.
    line1: sat.tle_line1,
    line2: sat.tle_line2,
  };
}

/**
 * Lazily parse and cache a satrec for a given satellite, using the TLE lines
 * already attached to its R-tree box. Returns null if no TLE is available
 * or parsing fails.
 */
export function getSatrec(satelliteId) {
  const id = String(satelliteId);
  if (satrecCache.has(id)) return satrecCache.get(id);

  const box = satelliteMap.get(id);
  if (!box?.line1 || !box?.line2) {
    satrecCache.set(id, null);
    return null;
  }

  try {
    const satrec = satellite.twoline2satrec(box.line1, box.line2);
    satrecCache.set(id, satrec);
    return satrec;
  } catch {
    satrecCache.set(id, null);
    return null;
  }
}

export function hasTLE(satelliteId) {
  const box = satelliteMap.get(String(satelliteId));
  return Boolean(box?.line1 && box?.line2);
}

/**
 * Insert or update satellites in the R-tree.
 * Removes satellites that no longer exist in new data.
 * @param {Array<Object>} newSatellites Array of satellite data objects
 */
export function updateRTreeWithSatellites(newSatellites) {
  const newMap = new Map();
  let added = 0, updated = 0, removed = 0;

  for (const sat of newSatellites) {
    const newBox = to3DBox(sat);
    if (!newBox) continue;

    const id = newBox.satelliteId;
    newMap.set(id, newBox);

    const existingBox = satelliteMap.get(id);

    if (!existingBox) {
      active3DTree.insert(newBox);
      added++;
    } else if (JSON.stringify(existingBox) !== JSON.stringify(newBox)) {
      active3DTree.remove(existingBox);
      active3DTree.insert(newBox);
      updated++;
      satrecCache.delete(String(id)); // TLE may have changed — drop stale parse
    }
  }

  // Remove satellites not present in new list
  for (const [id, oldBox] of satelliteMap.entries()) {
    if (!newMap.has(id)) {
      active3DTree.remove(oldBox);
      satrecCache.delete(String(id));
      removed++;
    }
  }

  satelliteMap.clear();
  for (const [id, box] of newMap.entries()) {
    satelliteMap.set(id, box);
  }
  console.log(`[R-Tree] Sync complete. Added: ${added}, Updated: ${updated}, Removed: ${removed}`);
}

/**
 * Query the R-tree within a radius around a point (x, y, z), with optional filters.
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {number} radius Search radius (default: 100 km)
 * @param {number} altMin Minimum altitude (default: 0)
 * @param {number} altMax Maximum altitude (default: Infinity)
 * @param {Object} filter Optional filter { category, country }
 * @returns {Array<Object>}
 */
export function query3D(
  x,
  y,
  z,
  radius = 100,
  altMin = 0,
  altMax = Infinity,
  filter = {}
) {
  let candidates;

  if (
    typeof x === 'number' &&
    typeof y === 'number' &&
    typeof z === 'number' &&
    typeof radius === 'number'
  ) {
    // Spatial query with radius
    candidates = active3DTree.search({
      minX: x - radius,
      minY: y - radius,
      minZ: z - radius,
      maxX: x + radius,
      maxY: y + radius,
      maxZ: z + radius,
    });
  } else {
    // No spatial filtering — search all satellites
    candidates = Array.from(satelliteMap.values());
  }

  return candidates.filter(sat =>
    typeof sat.altitude === 'number' &&
    sat.altitude >= altMin &&
    sat.altitude <= altMax &&
    (!filter.category || sat.category === filter.category) &&
    (!filter.country || sat.country === filter.country) &&
    (!filter.name || sat.name.toLowerCase().includes(filter.name.toLowerCase())) &&
    (!filter.orbit_type || sat.orbit_type === filter.orbit_type)
  );
}

/**
 * Load initial satellite data from JSON and populate the R-tree.
 * TLE data now arrives embedded in this same file — no separate fetch.
 */
export async function loadInitialSatellites() {
  console.log("LOADING ALL SATELLITES");
  try {
    const res = await fetch('/data/rtree_satellites.json');
    const json = await res.json();
    const initialSatellites = json?.rtree_data || json?.satellites || [];
    updateRTreeWithSatellites(initialSatellites);
    
  } catch (err) {
    console.error('[R-Tree] Failed to load initial satellites:', err);
  }
}

export function queryAllSatellites(filter = {}) {
  const { matchMode = 'exact', name, category, orbit_type } = filter;

  // Pass entire filter object to your spatial query
  const rawResults = query3D(undefined, undefined, undefined, undefined, 0, Infinity, filter);

  return rawResults.filter((sat) => {
    // Filter by name if provided
    if (name) {
      const satName = sat.name?.toLowerCase() || '';
      const filterName = name.toLowerCase();
      if (matchMode === 'startsWith') {
        if (!satName.startsWith(filterName)) return false;
      } else {
        if (satName !== filterName) return false;
      }
    }

    // Filter by category if provided
    if (category) {
      const satCategory = (sat.category || '').toLowerCase();
      if (satCategory !== category.toLowerCase()) return false;
    }

    if (orbit_type) {
      if (sat.orbit_type !== orbit_type) return false;
    }

    return true;
  }).map(({ minX, minY, minZ, maxX, maxY, maxZ, ...sat }) => sat);
}

export { active3DTree, satelliteMap };